import React from "react";
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  useTheme,
  Chip,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useCompanyKeyStatistics } from "../hooks/useCompanyKeyStatistics";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";

// Flat UI Color System - Theme Aware (copied from ViewTickerV2)
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    primary: "#22c55e",
    primaryHover: "#16a34a",
    negative: "#ef4444",
    negativeHover: "#dc2626",
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "#2a2a2a" : "#f8fafc",
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
    borderSubtle: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    textPrimary: isDark ? "#e4e6ea" : "#0f172a",
    textSecondary: isDark ? "#9ca3af" : "#64748b",
    textMuted: isDark ? "#6b7280" : "#94a3b8",
    accent: isDark ? "#60a5fa" : "#3b82f6",
    warning: isDark ? "#fbbf24" : "#f59e0b",
  };
};

// Flat UI Typography (simplified)
const flatUITypography = {
  h6: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.35 },
  body: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.43 },
  label: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.66,
    textTransform: "uppercase" as const,
  },
  caption: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.43 },
};

interface CompanyKeyStatisticsProps {
  ticker: string;
}

interface MetricRowProps {
  label: string;
  value: string | number;
  suffix?: string;
  isPercentage?: boolean;
  isCurrency?: boolean;
}

const MetricRow: React.FC<MetricRowProps> = ({
  label,
  value,
  suffix = "",
  isPercentage = false,
  isCurrency = false,
}) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);
  const { currencySymbol } = useStockCurrency();

  const formatValue = () => {
    if (typeof value === "number") {
      const prefix = isCurrency ? currencySymbol : "";

      if (isPercentage) {
        return `${(value * 100).toFixed(2)}%`;
      }
      // Trillions (>= 1,000,000,000,000)
      if (Math.abs(value) >= 1000000000000) {
        return `${prefix}${(value / 1000000000000).toFixed(2)}T`;
      }
      // Billions (>= 1,000,000,000)
      if (Math.abs(value) >= 1000000000) {
        return `${prefix}${(value / 1000000000).toFixed(1)}B`;
      }
      // Millions (>= 1,000,000)
      if (Math.abs(value) >= 1000000) {
        return `${prefix}${(value / 1000000).toFixed(1)}M`;
      }
      // Thousands (>= 1,000)
      if (Math.abs(value) >= 1000) {
        return `${prefix}${(value / 1000).toFixed(1)}K`;
      }
      // For smaller numbers, use locale string formatting with commas
      if (Number.isInteger(value)) {
        return `${prefix}${value.toLocaleString()}`;
      }
      return `${prefix}${value.toFixed(2)}`;
    }
    return value;
  };

  return (
    <Box
      sx={{
        py: 0.5,
        px: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start", // Align to top for multiline values
        gap: 1.5, // Minimum spacing (12px) between label and value
        borderBottom: `1px solid ${flatUIColors.borderSubtle}`,
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 400,
          color: flatUIColors.textSecondary,
          textTransform: "none",
          flexShrink: 0, // Prevent label from being crushed
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: flatUIColors.textPrimary,
          textAlign: "right",
          whiteSpace: "nowrap", // Prevent numbers from breaking mid-value (e.g., "203." / "8M")
        }}
      >
        {formatValue()}
        {suffix}
      </Typography>
    </Box>
  );
};

interface MiniTableProps {
  title: string;
  metrics: Array<{
    label: string;
    value: string | number;
    suffix?: string;
    isPercentage?: boolean;
    isCurrency?: boolean;
  }>;
}

const MiniTable: React.FC<MiniTableProps> = ({ title, metrics }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f8f9fa",
        border: `1px solid ${flatUIColors.border}`,
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5, // Reduced from 2
          py: 1, // Reduced from 1.5
          backgroundColor:
            theme.palette.mode === "dark" ? "#212121" : "#f1f5f9",
          borderBottom: `1px solid ${flatUIColors.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem", // Reduced from 0.8rem
            fontWeight: 600,
            color: flatUIColors.textPrimary,
            textTransform: "uppercase",
            letterSpacing: "0.3px", // Reduced from 0.5px
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Metrics */}
      <Box sx={{ px: 1.5 }}>
        {metrics.map((metric, index) => (
          <MetricRow
            key={index}
            label={metric.label}
            value={metric.value}
            suffix={metric.suffix}
            isPercentage={metric.isPercentage}
            isCurrency={metric.isCurrency}
          />
        ))}
      </Box>
    </Box>
  );
};

// Helper function to create metric objects
const createMetric = (
  label: string,
  value: string | number,
  options: {
    suffix?: string;
    isPercentage?: boolean;
    isCurrency?: boolean;
  } = {}
) => ({
  label,
  value,
  suffix: options.suffix ?? "",
  isPercentage: options.isPercentage ?? false,
  isCurrency: options.isCurrency ?? false,
});

const SKELETON_TABLES = [
  { title: "Profile", labels: ["Market Cap", "Enterprise Value", "Shares Outstanding", "Employees", "Sector", "Industry"] },
  { title: "Valuation", labels: ["P/E Ratio", "Forward P/E (1Y)", "Forward P/E (2Y)", "PEG Ratio", "P/S Ratio", "P/B Ratio", "P/FCF", "EV/Sales", "EV/EBITDA"] },
  { title: "Profitability", labels: ["Gross Margin", "Operating Margin", "Net Margin", "FCF Margin"] },
  { title: "Financial Health", labels: ["Cash", "Total Debt", "Net Debt", "Debt/Equity", "EBIT/Interest"] },
  { title: "Dividends", labels: ["Dividend Yield", "Payout Ratio", "DPS", "Growth (3Y)", "Growth (5Y)"] },
  { title: "Growth (CAGR)", labels: ["Revenue (3Y)", "Revenue (5Y)", "EPS (3Y)", "EPS (5Y)"] },
];

const SkeletonMiniTable: React.FC<{ title: string; labels: string[] }> = ({ title, labels }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f8f9fa",
        border: `1px solid ${flatUIColors.border}`,
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: theme.palette.mode === "dark" ? "#212121" : "#f1f5f9",
          borderBottom: `1px solid ${flatUIColors.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: flatUIColors.textPrimary,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 1.5 }}>
        {labels.map((label, i) => (
          <Box
            key={label}
            sx={{
              py: 0.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1.5,
              borderBottom: i < labels.length - 1 ? `1px solid ${flatUIColors.borderSubtle}` : "none",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 400,
                color: flatUIColors.textSecondary,
              }}
            >
              {label}
            </Typography>
            <Skeleton variant="text" width={60} height={18} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const CompanyKeyStatistics: React.FC<CompanyKeyStatisticsProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);
  const {
    data: statistics,
    isLoading,
    error,
  } = useCompanyKeyStatistics(ticker);

  if (isLoading) {
    return (
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            fontWeight: 700,
            color: flatUIColors.textPrimary,
            mb: 2,
          }}
        >
          Company Key Statistics
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 1.5,
          }}
        >
          {SKELETON_TABLES.map((table) => (
            <SkeletonMiniTable key={table.title} title={table.title} labels={table.labels} />
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            fontWeight: 700,
            color: flatUIColors.textPrimary,
            mb: 2,
          }}
        >
          Company Key Statistics
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: flatUIColors.textMuted,
          }}
        >
          Statistics are currently unavailable for this company.
        </Typography>
      </Box>
    );
  }

  if (!statistics) {
    return null;
  }

  const {
    profile,
    valuation,
    profitability,
    financialHealth,
    dividends,
    growth,
    metadata,
  } = statistics;

  // Prepare mini tables data
  const miniTables = [];

  // Profile table
  if (profile) {
    miniTables.push({
      title: "Profile",
      metrics: [
        createMetric("Market Cap", profile.marketCap, { isCurrency: true }),
        createMetric("Enterprise Value", profile.enterpriseValue, {
          isCurrency: true,
        }),
        createMetric("Shares Outstanding", profile.sharesOutstanding),
        createMetric("Employees", profile.employees?.toLocaleString() || "N/A"),
        createMetric("Sector", profile.sector),
        createMetric("Industry", profile.industry),
      ],
    });
  }

  // Valuation table
  if (valuation) {
    miniTables.push({
      title: "Valuation",
      metrics: [
        createMetric("P/E Ratio", valuation.peRatio, { suffix: "x" }),
        createMetric("Forward P/E (1Y)", valuation.forwardPE ?? "-", valuation.forwardPE != null ? { suffix: "x" } : {}),
        createMetric("Forward P/E (2Y)", valuation.forwardPE2Y ?? "-", valuation.forwardPE2Y != null ? { suffix: "x" } : {}),
        createMetric("PEG Ratio", valuation.pegRatio ?? "-", valuation.pegRatio != null ? { suffix: "x" } : {}),
        createMetric("P/S Ratio", valuation.priceToSales, { suffix: "x" }),
        createMetric("P/B Ratio", valuation.priceToBook, { suffix: "x" }),
        createMetric("P/FCF", valuation.priceToFCF, { suffix: "x" }),
        createMetric("EV/Sales", valuation.evToSales, { suffix: "x" }),
        createMetric("EV/EBITDA", valuation.evToEBITDA, { suffix: "x" }),
      ],
    });
  }

  // Profitability table
  if (profitability) {
    miniTables.push({
      title: "Profitability",
      metrics: [
        createMetric("Gross Margin", profitability.grossMargin, {
          isPercentage: true,
        }),
        createMetric("Operating Margin", profitability.operatingMargin, {
          isPercentage: true,
        }),
        createMetric("Net Margin", profitability.netMargin, {
          isPercentage: true,
        }),
        createMetric("FCF Margin", profitability.fcfMargin, {
          isPercentage: true,
        }),
      ],
    });
  }

  // Financial Health table
  if (financialHealth) {
    miniTables.push({
      title: "Financial Health",
      metrics: [
        createMetric("Cash", financialHealth.cash, { isCurrency: true }),
        createMetric("Total Debt", financialHealth.totalDebt, {
          isCurrency: true,
        }),
        createMetric("Net Debt", financialHealth.netDebt, { isCurrency: true }),
        createMetric("Debt/Equity", financialHealth.debtToEquity, {
          suffix: "x",
        }),
        createMetric("EBIT/Interest", financialHealth.interestCoverage, {
          suffix: "x",
        }),
      ],
    });
  }

  // Dividends table
  if (dividends) {
    miniTables.push({
      title: "Dividends",
      metrics: [
        createMetric("Dividend Yield", dividends.dividendYield, {
          isPercentage: true,
        }),
        createMetric("Payout Ratio", dividends.payoutRatio, {
          isPercentage: true,
        }),
        createMetric("DPS", dividends.dividendPerShare, { isCurrency: true }),
        createMetric("Growth (3Y)", dividends.dividendGrowth3Y, {
          isPercentage: true,
        }),
        createMetric("Growth (5Y)", dividends.dividendGrowth5Y, {
          isPercentage: true,
        }),
      ],
    });
  }

  // Growth table
  if (growth) {
    miniTables.push({
      title: "Growth (CAGR)",
      metrics: [
        createMetric("Revenue (3Y)", growth.revenueCAGR3Y, {
          isPercentage: true,
        }),
        createMetric("Revenue (5Y)", growth.revenueCAGR5Y, {
          isPercentage: true,
        }),
        createMetric("EPS (3Y)", growth.epsCAGR3Y, { isPercentage: true }),
        createMetric("EPS (5Y)", growth.epsCAGR5Y, { isPercentage: true }),
      ],
    });
  }

  return (
    <Box>
      {/* Header */}
      <Typography
        variant="h6"
        sx={{
          fontSize: "1.1rem", // Reduced from 1.25rem
          fontWeight: 700,
          color: flatUIColors.textPrimary,
          mb: 2, // Reduced from 3
        }}
      >
        Company Key Statistics
      </Typography>

      {/* Data Quality Indicator */}
      {metadata.errors && metadata.errors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label="Partial Data Available"
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: 24,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 193, 7, 0.15)"
                  : "rgba(255, 193, 7, 0.1)",
              color: theme.palette.mode === "dark" ? "#FFC107" : "#E65100",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 193, 7, 0.3)"
                  : "rgba(255, 193, 7, 0.4)"
              }`,
            }}
          />
        </Box>
      )}

      {/* Mini Tables - 3 per row */}
      {miniTables.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 1.5, // Reduced from 2
          }}
        >
          {miniTables.map((table, index) => (
            <MiniTable
              key={index}
              title={table.title}
              metrics={table.metrics}
            />
          ))}
        </Box>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Company statistics data is currently unavailable.
        </Alert>
      )}
    </Box>
  );
};

export default React.memo(CompanyKeyStatistics);
